import React, { useState } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  Button,
  notification,
  Row,
  Col,
  Typography,
} from "antd";
import Api from "api";

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  type?: 'plant' | 'animal';
  suggestedName?: string;
}

const SpeciesSuggestionModal: React.FC<Props> = ({
  isOpen,
  onClose,
  type = 'plant',
  suggestedName = '',
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (values: any) => {
    setLoading(true);

    try {
      const response = await Api.post('/species-suggestions', {
        ...values,
        type,
        scientific_name: values.scientific_name || suggestedName
      });

      if (response.data.success) {
        notification.success({
          message: 'Species Suggestion Submitted',
          description: 'Thank you for your suggestion! Our team will review it and add it to the database if appropriate.',
          duration: 5
        });

        form.resetFields();
        onClose();
      }
    } catch (error: any) {
      console.error('Error submitting species suggestion:', error);

      const errorMessage =
        error.response?.data?.messages?.[0] ||
        'Failed to submit species suggestion. Please try again.';

      notification.error({
        message: 'Submission Failed',
        description: errorMessage,
        duration: 4
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={`Suggest a New ${type === 'plant' ? 'Plant' : 'Animal'} Species`}
      open={isOpen}
      onCancel={handleCancel}
      footer={null}
      width={600}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          scientific_name: suggestedName,
          type
        }}
      >
        <Row gutter={16}>
          <Col span={24}>
            <Text type="secondary">
              Help us expand our database by suggesting species that are missing.
              Please provide as much information as possible to help our team verify and add the species.
            </Text>
          </Col>
        </Row>

        <Row gutter={16} style={{ marginTop: 16 }}>
          <Col span={12}>
            <Form.Item
              label="Scientific Name"
              name="scientific_name"
              rules={[
                { required: true, message: 'Scientific name is required' },
                { min: 3, message: 'Scientific name must be at least 3 characters' }
              ]}
            >
              <Input placeholder="e.g., Corymbia ficifolia" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Type"
              name="type"
            >
              <Select disabled>
                <Option value="plant">Plant</Option>
                <Option value="animal">Animal</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Common Names"
              name="common_names"
            >
              <Input placeholder="e.g., Red flowering gum" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Family"
              name="family_name"
            >
              <Input placeholder="e.g., Myrtaceae" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Genus"
              name="genus"
            >
              <Input placeholder="e.g., Corymbia" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Species"
              name="species"
            >
              <Input placeholder="e.g., ficifolia" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label="Conservation Status"
          name="conservation_status"
        >
          <Input placeholder="e.g., Common, Rare, Endangered" />
        </Form.Item>

        <Form.Item
          label="Habitat/Location"
          name="habitat_location"
        >
          <Input placeholder="e.g., Native to Western Australia, cultivated in California" />
        </Form.Item>

        <Form.Item
          label="Reason for Suggestion"
          name="reason"
        >
          <TextArea
            rows={3}
            placeholder="Why should this species be added? (e.g., commonly found in local gardens, important for ecosystem, etc.)"
          />
        </Form.Item>

        <Form.Item
          label="References/Sources"
          name="references"
        >
          <TextArea
            rows={2}
            placeholder="Any scientific papers, field guides, or reliable sources that document this species"
          />
        </Form.Item>

        <Form.Item
          label="Additional Notes"
          name="notes"
        >
          <TextArea
            rows={3}
            placeholder="Any other information that might be helpful for our team to verify this species"
          />
        </Form.Item>

        <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
          <Button
            onClick={handleCancel}
            style={{ marginRight: 8 }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
          >
            Submit Suggestion
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default SpeciesSuggestionModal;